import { useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from '../redux/store';
import { useLazyGetAccountTokenQuery } from '../redux/api/accountApi';
import { useLazyGetPublicServicesQuery, useLazyGetServicesQuery } from '../redux/api/serviceApi';
import { getCurrentAccountId } from '../utils/tokenUtils';
import { deleteRecentAccountId, getRecentAccountId } from '../utils/recentAccount';
import { setFrameworkMode, setInit } from '../redux/slices/layoutSlice';
import {
  deleteAccountToken,
  getAccountToken as getAccountTokenFromStorage,
  isAuth,
  updateTokens,
} from '../utils/tokenServices';

const EXPIRED_ACCOUNT_TOKEN = 'OSTS_ACCOUNT_TOKEN_EXPIRED';
const EXPIRED_USER_TOKEN = 'OSTS_USER_TOKEN_EXPIRED';

export const useTokenManagement = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const currentAccountId = useAppSelector(getCurrentAccountId);
  const recentAccountId = useAppSelector(getRecentAccountId);
  const accountToken = useAppSelector(getAccountTokenFromStorage);
  const [getAccountToken] = useLazyGetAccountTokenQuery();
  const [getServicesList] = useLazyGetServicesQuery();
  const [getPublicService] = useLazyGetPublicServicesQuery();

  const fetchServices = async (isForAccount: boolean) => {
    await getServicesList({ isForAccount });
    dispatch(setInit(false));
  };

  const backToUserMode = async () => {
    dispatch(setFrameworkMode('USER'));
    await fetchServices(false);
    deleteAccountToken();
    deleteRecentAccountId();
    navigate('/');
  };

  const claimAccountToken = async (recentAccountId: number) => {
    await getAccountToken({ accountId: recentAccountId }).unwrap();
    dispatch(setFrameworkMode('ACCOUNT'));
    await fetchServices(true);
  };

  useEffect(() => {
    const handleAuth = async () => {
      if (!isAuth()) {
        await getPublicService();
        dispatch(setInit(false));
        return;
      }

      if (recentAccountId && !currentAccountId) {
        claimAccountToken(recentAccountId);
        return;
      }

      if (recentAccountId === currentAccountId) {
        await fetchServices(!!currentAccountId);
        return;
      }

      if (accountToken || (recentAccountId && currentAccountId && recentAccountId !== currentAccountId)) {
        dispatch(setFrameworkMode('ACCOUNT'));
        await fetchServices(true);
        return;
      }

      if (isAuth() && !accountToken && !recentAccountId) {
        backToUserMode();
        return;
      }
    };

    handleAuth();
  }, [currentAccountId, dispatch, getPublicService, getServicesList, isAuth(), recentAccountId, accountToken]);

  const updateAccountToken = useCallback(() => {
    const accountId = getCurrentAccountId();

    if (!accountId) {
      navigate('/select-account');
    } else {
      getAccountToken({ accountId });
    }
  }, [getAccountToken, navigate]);

  const updateUserToken = () => {
    updateTokens();
  };

  useEffect(() => {
    window.addEventListener(EXPIRED_ACCOUNT_TOKEN, updateAccountToken);
    window.addEventListener(EXPIRED_USER_TOKEN, updateUserToken);

    return () => {
      window.removeEventListener(EXPIRED_ACCOUNT_TOKEN, updateAccountToken);
      window.removeEventListener(EXPIRED_USER_TOKEN, updateUserToken);
    };
  }, []);
};

export default useTokenManagement;
